import { useState, useRef, useCallback } from 'react'
import EasyCropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Camera, Upload, Edit, Trash2, Loader2 } from 'lucide-react'
import { userService } from '../services/userService'

export interface ProfilePhotoUploadProps {
  currentPhoto?: string
  onPhotoChange: (photo: string) => void
  onPhotoRemove?: () => void
  onError?: (error: Error) => void
  onSuccess?: (message: string) => void
}

const OUTPUT_SIZE = 480

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', reject)
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = url
  })
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE
  const ctx = canvas.getContext('2d')!

  const imageWidth = image.naturalWidth || image.width
  const imageHeight = image.naturalHeight || image.height

  const cropLeft = pixelCrop.x
  const cropTop = pixelCrop.y
  const cropRight = pixelCrop.x + pixelCrop.width
  const cropBottom = pixelCrop.y + pixelCrop.height

  const srcX = Math.max(0, cropLeft)
  const srcY = Math.max(0, cropTop)
  const srcRight = Math.min(imageWidth, cropRight)
  const srcBottom = Math.min(imageHeight, cropBottom)
  const srcWidth = Math.max(0, srcRight - srcX)
  const srcHeight = Math.max(0, srcBottom - srcY)

  ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  if (srcWidth > 0 && srcHeight > 0) {
    const dstX = ((srcX - cropLeft) / pixelCrop.width) * OUTPUT_SIZE
    const dstY = ((srcY - cropTop) / pixelCrop.height) * OUTPUT_SIZE
    const dstWidth = (srcWidth / pixelCrop.width) * OUTPUT_SIZE
    const dstHeight = (srcHeight / pixelCrop.height) * OUTPUT_SIZE

    ctx.drawImage(image, srcX, srcY, srcWidth, srcHeight, dstX, dstY, dstWidth, dstHeight)
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))),
      'image/jpeg',
      0.9,
    )
  })
}

export const ProfilePhotoUpload = ({
  currentPhoto,
  onPhotoChange,
  onPhotoRemove,
  onError,
  onSuccess,
}: ProfilePhotoUploadProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      onError?.(new Error('Please select an image file.'))
      return
    }

    setIsReading(true)
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        setIsReading(false)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setImageSrc(reader.result as string)
        setIsDialogOpen(true)
      }
      img.onerror = () => {
        setIsReading(false)
        onError?.(new Error('Failed to load image.'))
      }
      img.src = reader.result as string
    }
    reader.onerror = () => {
      setIsReading(false)
      onError?.(new Error('Failed to read file.'))
    }
    reader.readAsDataURL(file)
  }

  const handleSaveCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return

    setIsUploading(true)
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels)
      const croppedImage = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
      })

      const { avatarUrl } = await userService.uploadCroppedAvatar(croppedImage)
      onPhotoChange(avatarUrl)
      setIsDialogOpen(false)
      setImageSrc(null)
      onSuccess?.('Your profile photo has been uploaded successfully.')
    } catch (e: any) {
      console.error(e)
      onError?.(e)
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    if (isUploading) return
    setIsDialogOpen(false)
    setImageSrc(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemovePhoto = () => {
    if (onPhotoRemove) {
      onPhotoRemove()
      onSuccess?.('Your profile photo has been removed.')
    }
  }

  return (
    <div className="space-y-4">
      <Label className="text-white">Profile Photo</Label>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="relative">
          <div className="h-32 w-32 rounded-md overflow-hidden bg-white/10 border-2 border-white/20 flex items-center justify-center">
            {isReading ? (
              <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
            ) : currentPhoto ? (
              <img src={currentPhoto} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <Camera className="h-12 w-12 text-gray-400" />
            )}
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <p className="text-sm text-gray-400">
            Upload a photo. You can crop and zoom after selecting it.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id="photo-upload"
          />
          <div className="flex flex-wrap gap-2">
            {currentPhoto ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#C0FD72]/50 text-[#C0FD72] hover:bg-[#C0FD72]/10"
                  disabled={isReading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isReading ? 'Loading...' : 'Replace Photo'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#C0FD72]/50 text-[#C0FD72] hover:bg-[#C0FD72]/10"
                  disabled={isReading}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                {onPhotoRemove && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRemovePhoto}
                    className="border-[#C0FD72]/50 text-[#C0FD72] hover:bg-[#C0FD72]/10"
                    disabled={isReading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[#C0FD72]/50 text-[#C0FD72] hover:bg-[#C0FD72]/10"
                disabled={isReading}
              >
                {isReading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {isReading ? 'Loading...' : 'Upload Photo'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !isUploading && setIsDialogOpen(open)}>
        <DialogContent className="bg-[#1F2937] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Adjust Your Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative h-80 w-full rounded-lg overflow-hidden bg-black">
              {imageSrc && (
                <EasyCropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={0.8}
                  aspect={1}
                  cropShape="rect"
                  showGrid={false}
                  restrictPosition={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-10">Zoom</span>
              <input
                type="range"
                min={0.8}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-[#C0FD72]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="border-white/20 text-white hover:bg-white/10"
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveCrop}
              className="bg-[#C0FD72] text-black hover:bg-[#C0FD72]/90"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Photo'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
